import React, { useState } from 'react';
import classNames from 'classnames';
// We can reuse the styling from exclusive-buttons since it's identical visually
import styles from '../exclusive-buttons/exclusive-buttons.module.scss';


export interface InclusiveButtonsProps<T, K = T> {
  items: readonly T[];
  value?: K[];
  onChange?: (value: K[]) => void;
  onBlur?: () => void;
  identityFn?: (item: T) => K;
  textFn?: (item: T) => React.ReactNode;
  disabled?: boolean;
  readonly?: boolean;
  className?: string;
  renderItem?: (item: T) => React.ReactNode;
}

export function InclusiveButtons<T, K = T>({
  items,
  value = [],
  onChange,
  onBlur,
  identityFn = (item) => item as unknown as K,
  textFn = (item) => String(item),
  disabled = false,
  readonly = false,
  className,
  renderItem
}: InclusiveButtonsProps<T, K>) {
  const [focusedValue, setFocusedValue] = useState<K | null>(null);

  const toggleItem = (itemValue: K) => {
    if (disabled || readonly) return;
    const isSelected = value.includes(itemValue);
    let newValue: K[];
    
    if (isSelected) {
      newValue = value.filter(v => v !== itemValue);
    } else {
      newValue = [...value, itemValue];
    }
    
    onChange?.(newValue);
  };

  return (
    <div className={classNames(styles.exclusiveButtons, className)}>
      {items.map((item) => {
        const itemValue = identityFn(item);
        const isSelected = value.includes(itemValue);
        const isFocused = itemValue === focusedValue;

        return (
          <label
            key={String(itemValue)}
            className={classNames('control button', styles.button, {
              'is-primary': isSelected,
              'is-selected': isSelected,
              'is-focused': isFocused,
            })}
            aria-disabled={disabled || readonly}
          >
            <input
              type="checkbox"
              className={styles.visuallyHidden}
              value={String(itemValue)}
              checked={isSelected}
              disabled={disabled || readonly}
              onChange={() => toggleItem(itemValue)}
              onFocus={() => setFocusedValue(itemValue)}
              onBlur={() => {
                setFocusedValue(null);
                onBlur?.();
              }}
            />
            {renderItem ? renderItem(item) : textFn(item)}
          </label>
        );
      })}
    </div>
  );
}
